import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  departmentsTooltip,
  originsTooltip,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const SpaLostDeniedReasons = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [reasonDescription, setReasonDescription] = useState<string | null>(
    null,
  );
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [reasonDescriptionError, setReasonDescriptionError] =
    useState<boolean>(false);
  const [reasonDescriptionErrorMessage, setReasonDescriptionErrorMessage] =
    useState<string>("");

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateReasonDescription = (): boolean => {
    if (!reasonDescription || reasonDescription.length === 0) {
      setAlertMessage("Reason Description is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setReasonDescriptionError(false);
    setReasonDescriptionErrorMessage("");
    if (
      reasonDescription &&
      !validateString(
        reasonDescription,
        validationLength.lostDeniedDescription,
        validationType.SPACE,
      )
    ) {
      setReasonDescriptionError(true);
      setReasonDescriptionErrorMessage(
        validationMessages.lostDeniedDescription,
      );
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.REASONDESCRIPTION,
        reasonDescription,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  const updateItem = () => {
    if (!validateReasonDescription()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${SpaPropertyEnum.lostDeniedReasons} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateReasonDescription()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setReasonDescription(list["Reason Description"] ?? null);
      setActive(parseSpaBooleanField(list?.Active ?? list?.IsActive));
    } else {
      setReasonDescription(null);
      setActive(true);
      setReasonDescriptionError(false);
      setReasonDescriptionErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Reason Description": reasonDescription,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Lost Denied Reasons
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.lostDeniedReasonHeader ??
                  originsTooltip?.active ??
                  "Lost Denied Reasons",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Reason Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.lostDeniedReasonLostDeniedReason,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Reason Description"
              value={reasonDescription ?? ""}
              onChange={(e) => setReasonDescription(e.target.value || null)}
              error={reasonDescriptionError}
              helperText={
                reasonDescriptionError ? reasonDescriptionErrorMessage : ""
              }
              fullWidth
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Reason
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Lost Denied Reasons Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.REASONDESCRIPTION}
              name={DNDFieldsEnum.ACTIVE}
            />
          ) : (
            <Typography variant="body2">
              No Lost Denied Reasons Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaLostDeniedReasons;
